import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatIDR } from "@/lib/api";
import SlotMachine from "@/components/SlotMachine";
import ZeusJackpot from "@/components/ZeusJackpot";
import { Button } from "@/components/ui/button";
import { LogOut, AlertTriangle, TrendingDown } from "lucide-react";

const BET_OPTIONS = [50_000, 100_000, 250_000, 500_000, 1_000_000];
const POLL_INTERVAL = 3000;

export default function PlayerPage() {
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [bet, setBet] = useState(50_000);
  const [spinning, setSpinning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [reels, setReels] = useState(["🍒", "💎", "7"]);
  const [showZeus, setShowZeus] = useState(false);
  const containerRef = useRef(null);

  // Load player from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("player");
    if (!saved) {
      navigate("/");
      return;
    }
    setPlayer(JSON.parse(saved));
  }, [navigate]);

  // Poll for balance & active state
  useEffect(() => {
    if (!player?.id) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const { data } = await api.get(`/players/me/${player.id}`);
        if (cancelled) return;
        if (!data.is_active) {
          localStorage.removeItem("player");
          toast.error("Anda telah dikeluarkan dari sesi oleh admin.");
          navigate("/reveal");
          return;
        }
        // Only update balance silently when not currently spinning
        setPlayer((prev) => (prev ? { ...prev, ...data } : data));
      } catch (err) {
        if (err?.response?.status === 404) {
          localStorage.removeItem("player");
          toast.error("Sesi sudah di-reset oleh admin.");
          navigate("/reveal");
        }
      }
    };

    const id = setInterval(poll, POLL_INTERVAL);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [player?.id, navigate]);

  const handleSpin = async () => {
    if (!player || spinning) return;
    if (player.balance < bet) {
      toast.error("Saldo tidak cukup untuk taruhan ini");
      return;
    }
    setSpinning(true);
    setLastResult(null);

    try {
      const { data } = await api.post(`/players/${player.id}/spin`, {
        bet_amount: bet,
      });

      // Animate ~2.5s before revealing result
      setTimeout(() => {
        setReels(data.symbols);
        setPlayer((prev) => ({ ...prev, balance: data.new_balance }));
        setLastResult(data);
        setSpinning(false);

        if (data.won) {
          toast.success(`JACKPOT! +${formatIDR(data.payout)}`);
          // Shake screen + Zeus lightning effect
          if (containerRef.current) {
            containerRef.current.classList.add("shake");
            setTimeout(() => containerRef.current?.classList.remove("shake"), 800);
          }
          setShowZeus(true);
          setTimeout(() => setShowZeus(false), 1800);
        } else {
          toast.error(`Kalah! -${formatIDR(bet)}`);
        }
      }, 2500);
    } catch (err) {
      setSpinning(false);
      toast.error(err?.response?.data?.detail || "Gagal melakukan spin");
    }
  };

  const handleExit = () => {
    localStorage.removeItem("player");
    navigate("/");
  };

  if (!player) return null;

  const netLoss = (player.initial_balance || 5_000_000) - player.balance;
  const canBet = player.balance >= bet && !spinning;

  return (
    <div ref={containerRef} className="casino-theme">
      <ZeusJackpot show={showZeus} />
      <div className="max-w-2xl mx-auto p-4 sm:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-xs uppercase tracking-widest text-stone-400">Pemain</p>
            <h2 className="heading text-2xl text-white" data-testid="player-name-display">
              {player.name}
            </h2>
          </div>
          <Button
            data-testid="exit-button"
            variant="ghost"
            size="sm"
            onClick={handleExit}
            className="text-stone-400 hover:text-red-400 hover:bg-red-500/10"
          >
            <LogOut className="w-4 h-4 mr-1.5" />
            Keluar
          </Button>
        </div>

        {/* Balance */}
        <div className="glass-card p-5 mb-6 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-amber-300/70 mb-1">Saldo</p>
            <p
              className="digital text-3xl sm:text-4xl"
              data-testid="player-balance"
            >
              {formatIDR(player.balance)}
            </p>
          </div>
          {netLoss > 0 && (
            <div className="text-right">
              <p className="text-xs uppercase tracking-widest text-red-300/70 mb-1 flex items-center justify-end gap-1">
                <TrendingDown className="w-3 h-3" /> Total Rugi
              </p>
              <p className="text-lg font-bold text-red-400" data-testid="player-loss">
                -{formatIDR(netLoss)}
              </p>
            </div>
          )}
        </div>

        {/* Slot machine */}
        <div className="glass-card gold-border p-4 sm:p-6 mb-6">
          <SlotMachine
            reels={reels}
            spinning={spinning}
            won={lastResult?.won}
          />

          {/* Result message */}
          <div className="mt-4 text-center min-h-[2rem]">
            {!spinning && lastResult && (
              <p
                data-testid="spin-result-message"
                className={`heading text-lg ${lastResult.won ? "text-amber-300" : "text-red-400"}`}
              >
                {lastResult.message}
              </p>
            )}
            {spinning && (
              <p className="text-stone-400 text-sm uppercase tracking-widest animate-pulse">
                Memutar reel...
              </p>
            )}
          </div>
        </div>

        {/* Bet selector */}
        <div className="mb-4">
          <p className="text-xs uppercase tracking-widest text-stone-400 mb-3 text-center">
            Pilih Taruhan
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {BET_OPTIONS.map((amt) => (
              <button
                key={amt}
                data-testid={`bet-option-${amt}`}
                onClick={() => setBet(amt)}
                disabled={spinning || player.balance < amt}
                className={`bet-chip py-2 text-xs sm:text-sm ${bet === amt ? "active" : ""}`}
              >
                {amt >= 1_000_000 ? "1jt" : `${amt / 1000}k`}
              </button>
            ))}
          </div>
        </div>

        {/* Spin button */}
        <button
          data-testid="spin-button"
          onClick={handleSpin}
          disabled={!canBet}
          className="spin-button w-full py-5 text-xl"
        >
          {spinning ? "SPINNING..." : `SPIN ${formatIDR(bet)}`}
        </button>

        {/* Warning if low balance */}
        {player.balance < 50_000 && (
          <div className="mt-4 flex items-center gap-2 text-xs text-red-300 bg-red-500/10 border border-red-500/30 p-3 rounded-lg">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>Saldo Anda tidak cukup untuk taruhan apa pun.</span>
          </div>
        )}
      </div>
    </div>
  );
}
